import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CHARACTER_SIZE,
  EVENTS,
  WRONG_CLICK_COOLDOWN_MS,
  type Artwork,
  type PlayerFoundReveal,
  type RoomSettings,
} from '@mimic/shared';
import { Check, Eye, Lock, Minus, Plus, Search, Target, Timer } from 'lucide-react';
import { socket } from '../lib/socket.js';
import { useGameStore } from '../store/gameStore.js';
import { PixelSprite } from './PixelSprite.js';
import { artworkBg } from './artworkBg.js';
import { Burst, Ripple } from '../components/effects.js';
import { HintOverlay } from './HintOverlay.js';
import { computeHintZones, hintRadius } from './hintZones.js';

const S = CHARACTER_SIZE;
const CLICK_MOVE_THRESHOLD = 6;
const CURSOR_EMIT_MS = 45;

interface Camera {
  zoom: number;
  x: number;
  y: number;
}

/** Un personnage à afficher sur l'œuvre (cible du chercheur ou co-caché). */
interface StagePlayer {
  id: string;
  x: number;
  y: number;
  rotation: number;
  pixels: number[];
  pseudo?: string;
}

/**
 * Plateau plein écran de la phase de recherche : l'œuvre remplit l'espace, avec
 * les personnages (`players`) dessus. Côté chercheur (`interactive`) : cibles à
 * cliquer, un raté impose un cooldown. Côté caché : il se voit lui-même et les
 * autres cachés (labels), et peut focaliser la caméra sur l'un d'eux (`focusTarget`).
 */
export function SeekerStage({
  artwork,
  interactive,
  totalHiders,
  players,
  showLabels = false,
  myId = null,
  focusTarget = null,
  settings,
  phaseStartedAt = null,
}: {
  artwork: Artwork;
  interactive: boolean;
  totalHiders: number;
  players: StagePlayer[];
  showLabels?: boolean;
  myId?: string | null;
  focusTarget?: { x: number; y: number; key: number } | null;
  /** Réglages de la partie (taille de zone, zoom progressif, indices). */
  settings: RoomSettings;
  /** Début de la phase de recherche (ms epoch serveur) pour le zoom progressif. */
  phaseStartedAt?: number | null;
}): JSX.Element {
  const outerRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  // Curseur du chercheur relayé par le serveur : montré aux joueurs qui regardent
  // la traque (jamais au chercheur lui-même, qui voit son vrai curseur).
  const remoteCursor = useGameStore((s) => s.seekerCursor);
  const lastCursor = useRef(0);

  // Zone de jeu FIXE, dimensionnée sur la fenêtre + le réglage de taille (pas de
  // ResizeObserver sur soi-même : évite la boucle de rétrécissement perpétuel).
  const [winSize, setWinSize] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 1280,
    h: typeof window !== 'undefined' ? window.innerHeight : 800,
  }));
  useEffect(() => {
    const on = () => setWinSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  const vp = useMemo(() => {
    const availW = Math.max(360, winSize.w - 240 - 32); // - sidebar - marges
    const availH = Math.max(320, winSize.h - 44 - 40); // - entête - marges
    return { w: availW, h: Math.max(320, Math.min(settings.boardSize, availH)) };
  }, [winSize.w, winSize.h, settings.boardSize]);

  const [cam, setCam] = useState<Camera>({ zoom: 1, x: 0, y: 0 });
  const [foundIds, setFoundIds] = useState<Set<string>>(new Set());
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  // Impact d'un clic, en coordonnées écran (relatives au plateau) : anime onde,
  // particules et libellé flottant pile à l'endroit cliqué.
  const [impact, setImpact] = useState<{ ok: boolean; x: number; y: number; key: number } | null>(
    null,
  );

  const fitScale = useMemo(
    () => Math.min(vp.w / artwork.width, vp.h / artwork.height),
    [vp.w, vp.h, artwork.width, artwork.height],
  );
  const scale = fitScale * cam.zoom;

  // Zoom progressif du chercheur : bloqué à ×1 puis un palier débloqué toutes les
  // `zoomStepSec`, jusqu'à `maxZoom`. Sinon plafond direct (min avec l'œuvre).
  const effMaxZoom = Math.min(artwork.maxZoom, settings.maxZoom);
  const elapsedSec = phaseStartedAt ? Math.max(0, (now - phaseStartedAt) / 1000) : 0;
  const allowedZoom =
    interactive && settings.progressiveZoom
      ? Math.min(effMaxZoom, 1 + Math.floor(elapsedSec / Math.max(1, settings.zoomStepSec)))
      : effMaxZoom;
  const nextUnlockIn =
    interactive && settings.progressiveZoom && allowedZoom < effMaxZoom
      ? Math.ceil(settings.zoomStepSec - (elapsedSec % settings.zoomStepSec))
      : null;

  // Horloge de la traque (zoom progressif + fraction pour les indices).
  useEffect(() => {
    if (!interactive) return;
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [interactive]);

  // Ne jamais dépasser le zoom autorisé (clamp quand un palier n'est pas encore débloqué).
  useEffect(() => {
    setCam((c) => (c.zoom > allowedZoom ? { ...c, zoom: allowedZoom } : c));
  }, [allowedZoom]);

  // Reset trouvailles à chaque œuvre (nouvelle manche).
  useEffect(() => {
    setFoundIds(new Set());
    setCooldownUntil(0);
    setImpact(null);
    setCam({ zoom: 1, x: 0, y: 0 });
  }, [artwork.id]);

  // Secoue brièvement le plateau (impact d'un clic). Redémarrage fiable via reflow.
  const shakeBoard = () => {
    const el = outerRef.current;
    if (!el) return;
    el.classList.remove('animate-shake');
    void el.offsetWidth;
    el.classList.add('animate-shake');
  };

  // Caméra centrée tant qu'on n'a pas zoomé (suit aussi le redimensionnement).
  useEffect(() => {
    setCam((c) =>
      c.zoom === 1
        ? {
            zoom: 1,
            x: (vp.w - artwork.width * fitScale) / 2,
            y: (vp.h - artwork.height * fitScale) / 2,
          }
        : c,
    );
  }, [artwork.id, vp.w, vp.h, fitScale, artwork.width, artwork.height]);

  // Marque les cachés trouvés (diffusé à toute la salle).
  useEffect(() => {
    const onFound = (data: PlayerFoundReveal) => {
      setFoundIds((s) => new Set(s).add(data.playerId));
    };
    socket.on(EVENTS.playerFound, onFound);
    return () => {
      socket.off(EVENTS.playerFound, onFound);
    };
  }, []);

  useEffect(() => {
    if (cooldownUntil <= Date.now()) return;
    const id = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(id);
  }, [cooldownUntil]);

  const cooldownLeft = Math.max(0, cooldownUntil - now);
  const onCooldown = cooldownLeft > 0;

  const drag = useRef<{ cx: number; cy: number; px: number; py: number; moved: boolean } | null>(
    null,
  );

  const onPointerDown = (e: React.PointerEvent) => {
    drag.current = { cx: e.clientX, cy: e.clientY, px: cam.x, py: cam.y, moved: false };
    try {
      viewportRef.current?.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };
  const emitCursor = (clientX: number, clientY: number) => {
    if (!interactive) return;
    const now = Date.now();
    if (now - lastCursor.current < CURSOR_EMIT_MS) return;
    lastCursor.current = now;
    const rect = viewportRef.current?.getBoundingClientRect();
    if (!rect) return;
    const ax = (clientX - rect.left - cam.x) / scale;
    const ay = (clientY - rect.top - cam.y) / scale;
    socket.emit(EVENTS.seekerCursor, { x: ax, y: ay });
  };

  const onPointerMove = (e: React.PointerEvent) => {
    emitCursor(e.clientX, e.clientY);
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.cx;
    const dy = e.clientY - d.cy;
    if (!d.moved && Math.abs(dx) + Math.abs(dy) <= CLICK_MOVE_THRESHOLD) return;
    d.moved = true;
    setCam((c) => ({ ...c, x: d.px + dx, y: d.py + dy }));
  };
  const onPointerUp = (e: React.PointerEvent) => {
    const d = drag.current;
    drag.current = null;
    if (d && !d.moved && interactive) attemptFind(e.clientX, e.clientY);
  };

  const attemptFind = (clientX: number, clientY: number) => {
    if (onCooldown) return;
    const rect = viewportRef.current?.getBoundingClientRect();
    if (!rect) return;
    const sx = clientX - rect.left; // point de clic en coords écran (plateau)
    const sy = clientY - rect.top;
    const ax = (sx - cam.x) / scale;
    const ay = (sy - cam.y) / scale;
    socket.emit(EVENTS.seekerClick, { x: ax, y: ay }, (res) => {
      if (!res.ok) return;
      const ok = res.hit;
      setImpact({ ok, x: sx, y: sy, key: Date.now() });
      shakeBoard();
      if (!ok) {
        setCooldownUntil(Date.now() + WRONG_CLICK_COOLDOWN_MS);
        setNow(Date.now());
      }
    });
  };

  // Retire l'impact après l'animation (les effets se démontent proprement).
  useEffect(() => {
    if (!impact) return;
    const t = setTimeout(() => setImpact(null), 1100);
    return () => clearTimeout(t);
  }, [impact]);

  const zoomBy = (factor: number, px: number, py: number) => {
    setCam((c) => {
      const z = Math.max(1, Math.min(allowedZoom, c.zoom * factor));
      const s0 = fitScale * c.zoom;
      const s1 = fitScale * z;
      if (s0 === 0) return c;
      return { zoom: z, x: px - ((px - c.x) * s1) / s0, y: py - ((py - c.y) * s1) / s0 };
    });
  };
  const onWheel = (e: React.WheelEvent) => {
    const rect = viewportRef.current?.getBoundingClientRect();
    if (!rect) return;
    zoomBy(e.deltaY < 0 ? 1.15 : 1 / 1.15, e.clientX - rect.left, e.clientY - rect.top);
  };

  // Focalise la caméra sur un joueur (déclenché depuis la liste des cachés).
  useEffect(() => {
    if (!focusTarget) return;
    const z = Math.min(allowedZoom, 3);
    const s1 = fitScale * z;
    setCam({ zoom: z, x: vp.w / 2 - focusTarget.x * s1, y: vp.h / 2 - focusTarget.y * s1 });
  }, [focusTarget, fitScale, vp.w, vp.h, allowedZoom]);

  const cursor = interactive ? (onCooldown ? 'not-allowed' : 'crosshair') : 'grab';
  const shown = players;

  // Zones d'indice (chercheur, après HINT_START_FRAC) sur les cachés non trouvés.
  // Seulement si activées ET si la traque est minutée (sinon pas de fraction de temps).
  const elapsedFrac =
    settings.hintsEnabled && settings.seekingTimed && settings.seekingSec > 0
      ? Math.max(0, Math.min(1, elapsedSec / settings.seekingSec))
      : null;
  const hintR = interactive && elapsedFrac != null ? hintRadius(elapsedFrac, artwork) : null;
  const hintZones =
    hintR != null
      ? computeHintZones(
          players.filter((p) => !foundIds.has(p.id)).map((p) => ({ id: p.id, x: p.x, y: p.y })),
          hintR,
        )
      : [];

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="flex h-11 shrink-0 items-center justify-between border-b border-line bg-surface px-4 text-sm">
        <span className="flex items-center gap-1.5 font-semibold">
          {interactive ? (
            <>
              <Search className="h-4 w-4 shrink-0" />
              Repère les personnages fondus dans la toile et clique dessus (raté = 3 s d’attente)
            </>
          ) : (
            <>
              <Eye className="h-4 w-4 shrink-0" />
              Observe l’œuvre et mémorise les cachettes possibles
            </>
          )}
        </span>
        {interactive && (
          <span className="font-mono text-muted">
            Trouvés{' '}
            <span
              key={foundIds.size}
              className="animate-pop-in inline-block font-bold text-emerald-600"
            >
              {foundIds.size}
            </span>
            /{totalHiders}
          </span>
        )}
      </div>

      <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto p-4">
        <div
          ref={outerRef}
          className="relative overflow-hidden rounded-2xl bg-night-800 shadow-frame ring-1 ring-line/60"
          style={{ width: vp.w, height: vp.h }}
        >
          <div
            ref={viewportRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onWheel={onWheel}
            className="absolute inset-0"
            style={{ touchAction: 'none', cursor }}
          >
            <div
              className="absolute left-0 top-0 origin-top-left"
              style={{
                width: artwork.width * fitScale,
                height: artwork.height * fitScale,
                transform: `translate(${cam.x}px, ${cam.y}px) scale(${cam.zoom})`,
                background: artworkBg(artwork),
              }}
            >
              {/* Personnages sur l'œuvre (cibles du chercheur, ou co-cachés) */}
              {shown.map((t) => {
                const isFound = foundIds.has(t.id);
                const isSelf = myId != null && t.id === myId;
                return (
                  <div
                    key={t.id}
                    className="pointer-events-none absolute"
                    style={{
                      left: t.x * fitScale,
                      top: t.y * fitScale,
                      width: S * fitScale,
                      height: S * fitScale,
                    }}
                  >
                    <PixelSprite pixels={t.pixels} size={S * fitScale} rotation={t.rotation} />
                    {/* Label (côté caché) : pseudo au-dessus, soi-même surligné */}
                    {showLabels && t.pseudo && (
                      <>
                        <span
                          className={`absolute inset-0 rounded-sm ring-1 ${
                            isSelf ? 'ring-gold' : 'ring-white/40'
                          }`}
                        />
                        <span
                          className={`absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-semibold text-white shadow-soft ${
                            isSelf ? 'bg-gold' : 'bg-night/70'
                          }`}
                        >
                          {isSelf ? 'toi' : t.pseudo}
                        </span>
                      </>
                    )}
                    {isFound && (
                      <>
                        {/* Halo qui pulse pour attirer l'œil sur la capture */}
                        <span
                          className="animate-pulse-ring absolute left-1/2 top-1/2 rounded-full border-2 border-emerald-400"
                          style={{ width: S * fitScale, height: S * fitScale }}
                        />
                        <span className="animate-pop-in absolute inset-0 rounded-sm ring-2 ring-emerald-400" />
                        <span className="animate-pop-in absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-white shadow-soft">
                          <Check className="h-3 w-3" strokeWidth={3} />
                        </span>
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Zones d'indice « projecteur » (chercheur) */}
            <HintOverlay zones={hintZones} camX={cam.x} camY={cam.y} scale={scale} />

            {!interactive && remoteCursor && (
              <div
                className="pointer-events-none absolute z-20 -translate-x-1 -translate-y-1 transition-[left,top] duration-75 ease-linear"
                style={{
                  left: cam.x + remoteCursor.x * scale,
                  top: cam.y + remoteCursor.y * scale,
                }}
              >
                {/* Halo pulsant qui suit le chercheur : rend la traque « vivante » */}
                <span className="animate-pulse-ring absolute left-2 top-2 h-8 w-8 rounded-full border-2 border-gold" />
                <svg width="26" height="26" viewBox="0 0 24 24" className="relative drop-shadow-md">
                  <path
                    d="M5 3l14 8-6 1.5L9.5 19 5 3z"
                    fill="#f59e0b"
                    stroke="#1c1917"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="ml-4 inline-flex items-center gap-1 whitespace-nowrap rounded bg-gold/90 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                  <Search className="h-2.5 w-2.5" /> chercheur
                </span>
              </div>
            )}

            {/* Impact d'un clic : onde + particules + libellé flottant, pile au clic */}
            {impact && (
              <div
                key={impact.key}
                className="pointer-events-none absolute z-30"
                style={{ left: impact.x, top: impact.y }}
              >
                <Ripple
                  x={0}
                  y={0}
                  color={impact.ok ? 'rgba(52,211,153,0.85)' : 'rgba(248,113,113,0.85)'}
                />
                {impact.ok && <Burst x={0} y={0} count={14} />}
                <span
                  className={`animate-float-up absolute left-0 top-0 inline-flex items-center gap-1 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-bold text-white shadow-pop ${
                    impact.ok ? 'bg-emerald-500' : 'bg-red-500'
                  }`}
                >
                  {impact.ok ? (
                    <>
                      <Target className="h-4 w-4" /> Trouvé !
                    </>
                  ) : (
                    'Raté…'
                  )}
                </span>
              </div>
            )}

            {onCooldown && (
              <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-red-500/90 px-4 py-1 font-mono text-sm text-white">
                  <Timer className="h-4 w-4" /> {(cooldownLeft / 1000).toFixed(1)}s
                </span>
              </div>
            )}

            {/* Palier de zoom verrouillé (zoom progressif) : compte à rebours du prochain cran */}
            {interactive && nextUnlockIn != null && (
              <div className="pointer-events-none absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-lg bg-night/80 px-2.5 py-1 text-xs text-white">
                <Lock className="h-3.5 w-3.5 text-gold" />
                Zoom ×{allowedZoom + 1} dans {nextUnlockIn}s
              </div>
            )}

            <div className="absolute bottom-3 right-3 flex items-center gap-1 rounded-lg bg-surface/90 p-1 shadow-soft">
              <HudBtn onClick={() => zoomBy(1 / 1.3, vp.w / 2, vp.h / 2)}>
                <Minus className="h-4 w-4" />
              </HudBtn>
              <span className="w-12 text-center font-mono text-xs">
                {cam.zoom.toFixed(1)}×<span className="text-muted">/{allowedZoom}</span>
              </span>
              <HudBtn
                onClick={() => zoomBy(1.3, vp.w / 2, vp.h / 2)}
                disabled={cam.zoom >= allowedZoom - 0.01}
              >
                <Plus className="h-4 w-4" />
              </HudBtn>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function HudBtn({
  children,
  onClick,
  disabled = false,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="grid h-8 min-w-8 place-items-center rounded-md border border-line bg-surface px-2 text-sm transition hover:border-muted/40 disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}
